import type { SDUINode } from "../model/types";
import { ScopeProvider, truthy, useInput, useResolved, useSDUI } from "./context";

// ---------- registry ----------
export type SDUIComponent = (props: { node: SDUINode }) => React.ReactNode;
export const registry: Record<string, SDUIComponent> = {};
export function register(type: string, comp: SDUIComponent) { registry[type] = comp; }

/** Render one node: applies visibility, dispatches by type. */
export function RenderNode({ node }: { node: SDUINode }): React.ReactNode {
  const visibleVal = useResolved(node.visible);
  const Comp = registry[node.type];
  if (node.visible !== undefined && !truthy(visibleVal)) return null;
  if (!Comp) return <div data-sdui-unknown={node.type}>[unknown component: {node.type}]</div>;
  return <Comp node={node} />;
}

function children(node: SDUINode): SDUINode[] {
  return (node.children as SDUINode[] | undefined) ?? [];
}

// ---------- layout ----------
register("stack", ({ node }) => {
  const dir = (node.direction as string) ?? "vertical";
  const horizontal = dir === "horizontal";
  const style: React.CSSProperties = {
    display: "flex",
    flexDirection: horizontal ? "row" : "column",
    gap: gap(node.gap),
    // A horizontal stack is nearly always a row of controls — buttons next to a label, a filter beside
    // a field. Flex defaults to `stretch`, which made every button in such a row as tall as the tallest
    // sibling: a two-line paragraph next to two buttons produced two oversized buttons with wrapped
    // labels. Side-by-side content blocks use `grid` (which sizes its own columns), so centring here
    // costs nothing and fixes the common case. Wrapping keeps a narrow stage from overflowing.
    ...(horizontal ? { alignItems: "center", flexWrap: "wrap" as const } : null),
  };
  return (
    <div className="sdui-stack" style={style}>
      {children(node).map((c, i) => <RenderNode key={i} node={c} />)}
    </div>
  );
});

register("grid", ({ node }) => {
  const style: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: `repeat(${Number(node.columns) || 1}, 1fr)`,
    gap: gap(node.gap),
  };
  return <div className="sdui-grid" style={style}>{children(node).map((c, i) => <RenderNode key={i} node={c} />)}</div>;
});

register("card", ({ node }) => (
  <section className="sdui-card">
    {node.title ? <h3 className="sdui-card-title">{String(node.title)}</h3> : null}
    {children(node).map((c, i) => <RenderNode key={i} node={c} />)}
  </section>
));

register("tabs", ({ node }) => {
  const tabs = (node.tabs as Array<{ key: string; label: string; content: SDUINode }>) ?? [];
  const active = useResolved(node.active);
  const current = tabs.find((t) => t.key === active) ?? tabs[0];
  return (
    <div className="sdui-tabs">
      <div className="sdui-tab-labels">{tabs.map((t) => <span key={t.key} data-active={t.key === current?.key}>{t.label}</span>)}</div>
      {current ? <RenderNode node={current.content} /> : null}
    </div>
  );
});

register("modal", ({ node }) => {
  const open = truthy(useResolved(node.open));
  if (!open) return null;
  return (
    <div className="sdui-modal" role="dialog">
      {node.title ? <h3>{String(node.title)}</h3> : null}
      {children(node).map((c, i) => <RenderNode key={i} node={c} />)}
    </div>
  );
});

// ---------- display ----------
register("text", ({ node }) => {
  const v = useResolved(node.value);
  const variant = (node.variant as string) ?? "body";
  return <span className={`sdui-text sdui-text-${variant}`}>{fmt(v)}</span>;
});

register("divider", () => <hr className="sdui-divider" />);

register("image", ({ node }) => <img className="sdui-image" src={String(useResolved(node.src) ?? "")} alt={(node.alt as string) ?? ""} />);

/** The tones the stylesheet defines. Anything else falls back to neutral rather than emitting a
 *  class that does not exist — a tone computed per row is attacker-adjacent input (it comes from a
 *  sibling's expression), so it is never interpolated into the class name unchecked. */
const TONES = new Set(["neutral", "info", "success", "warning", "danger"]);

register("badge", ({ node }) => {
  // Resolved, not read raw: a badge inside a table cell needs its tone to depend on the row
  // ("danger" for CRITICAL, "success" for a resolved finding), which means an expression.
  const tone = String(useResolved(node.tone) ?? "neutral");
  const cls = TONES.has(tone) ? tone : "neutral";
  return <span className={`sdui-badge sdui-tone-${cls}`}>{fmt(useResolved(node.value))}</span>;
});

register("table", ({ node }) => {
  const rows = (useResolved(node.rows) as unknown[]) ?? [];
  const empty = useResolved(node.empty);
  const cols = (node.columns as Array<{ key: string; header: string; cell?: unknown; align?: string }>) ?? [];
  if (rows.length === 0 && node.empty !== undefined) {
    return <div className="sdui-table-empty">{fmt(empty)}</div>;
  }
  // Wrapped so a wide table scrolls instead of losing columns. `.sdui-table` carries
  // `overflow:hidden` for its rounded corners, which silently CLIPPED every column past the
  // container width — an app with a dozen columns lost the right-hand ones (status, due date)
  // with no scrollbar and no other sign that data was missing.
  return (
    <div className="sdui-table-wrap">
    <table className="sdui-table">
      <thead><tr>{cols.map((c) => <th key={c.key} style={{ textAlign: (c.align as any) ?? "left" }}>{c.header}</th>)}</tr></thead>
      <tbody>
        {rows.map((row, i) => (
          <ScopeProvider key={i} extra={{ row, item: row, index: i }}>
            <tr>{cols.map((c) => <TableCell key={c.key} col={c} row={row} />)}</tr>
          </ScopeProvider>
        ))}
      </tbody>
    </table>
    </div>
  );
});

/** A cell template that is a node (rather than a binding string) is rendered as one. Without this a
 *  table could only ever hold text — so a severity column had no way to be a coloured badge, and a
 *  reader had to compare the word "Critical" against the word "Medium" down a column of grey rows.
 *  The row is already in scope here (the ScopeProvider above), so the node's expressions see
 *  `row`/`item`/`index` exactly like a binding cell does. */
function isNode(v: unknown): v is SDUINode {
  return typeof v === "object" && v !== null && typeof (v as any).type === "string";
}

function TableCell({ col, row }: { col: { key: string; cell?: unknown; align?: string }; row: unknown }) {
  const node = isNode(col.cell) ? col.cell : undefined;
  // Called unconditionally (hook rules); a node cell resolves itself when rendered.
  const resolved = useResolved(node ? undefined : col.cell);
  const value = col.cell !== undefined ? resolved : (row as any)?.[col.key];
  return (
    <td style={{ textAlign: (col.align as any) ?? "left" }}>
      {node ? <RenderNode node={node} /> : fmt(value)}
    </td>
  );
}

register("list", ({ node }) => {
  const items = (useResolved(node.items) as unknown[]) ?? [];
  const empty = useResolved(node.empty);
  if (items.length === 0 && node.empty !== undefined) return <div className="sdui-list-empty">{fmt(empty)}</div>;
  return (
    <ul className="sdui-list">
      {items.map((item, index) => (
        <ScopeProvider key={index} extra={{ item, index }}>
          <li><RenderNode node={node.item as SDUINode} /></li>
        </ScopeProvider>
      ))}
    </ul>
  );
});

// ---------- inputs ----------
function labelWrap(node: SDUINode, control: React.ReactNode) {
  return (
    <label className="sdui-field">
      {node.label ? <span className="sdui-label">{String(node.label)}</span> : null}
      {control}
      {node.help ? <span className="sdui-help">{String(node.help)}</span> : null}
    </label>
  );
}

register("textField", ({ node }) => {
  const { value, onChange } = useInput(node);
  return labelWrap(node, <input className="sdui-input" type="text" value={(value as string) ?? ""} placeholder={(node.placeholder as string) ?? ""} onChange={(e) => onChange(e.target.value)} />);
});

register("numberField", ({ node }) => {
  const { value, onChange } = useInput(node);
  return labelWrap(node, <input className="sdui-input" type="number" value={(value as number | string) ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} />);
});

register("textArea", ({ node }) => {
  const { value, onChange } = useInput(node);
  return labelWrap(node, <textarea className="sdui-input" rows={(node.rows as number) ?? 3} value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value)} />);
});

register("select", ({ node }) => {
  const { value, onChange } = useInput(node);
  const options = (useResolved(node.options) as Array<{ label: string; value: unknown }>) ?? [];
  return labelWrap(node, (
    <select className="sdui-input" value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}>
      {options.map((o, i) => <option key={i} value={String(o.value)}>{o.label}</option>)}
    </select>
  ));
});

register("checkbox", ({ node }) => {
  const { value, onChange } = useInput(node);
  return labelWrap(node, <input className="sdui-checkbox" type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />);
});

register("switch", ({ node }) => {
  const { value, onChange } = useInput(node);
  return labelWrap(node, <input className="sdui-switch" type="checkbox" role="switch" checked={!!value} onChange={(e) => onChange(e.target.checked)} />);
});

register("radioGroup", ({ node }) => {
  const { value, onChange } = useInput(node);
  const options = (node.options as Array<{ label: string; value: unknown }>) ?? [];
  return labelWrap(node, (
    <div className="sdui-radiogroup" role="radiogroup">
      {options.map((o, i) => (
        <label key={i}><input type="radio" checked={value === o.value} onChange={() => onChange(o.value)} />{o.label}</label>
      ))}
    </div>
  ));
});

register("datePicker", ({ node }) => {
  const { value, onChange } = useInput(node);
  const mode = (node.mode as string) ?? "date";
  const type = mode === "datetime" ? "datetime-local" : mode === "time" ? "time" : "date";
  return labelWrap(node, <input className="sdui-input" type={type} value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value)} />);
});

// ---------- action ----------
register("button", ({ node }) => {
  const { runner } = useSDUI();
  const disabled = truthy(useResolved(node.disabled));
  const kind = (node.kind as string) ?? "secondary";
  return (
    <button
      className={`sdui-button sdui-button-${kind}`}
      disabled={node.disabled !== undefined ? disabled : undefined}
      onClick={() => void runner.run(node.onTap as any)}
    >
      {String(node.label)}
    </button>
  );
});

// ---------- special ----------
register("sign", ({ node }) => {
  const { runner } = useSDUI();
  const doRef = node.documentRef;
  const onClick = () => void runner.run([{ op: "sign", documentRef: doRef, assuranceRequired: node.assuranceRequired, onSuccess: node.onComplete } as any]);
  return <button className="sdui-sign" onClick={onClick}>{(node.label as string) ?? "전자서명"}</button>;
});

register("remote", ({ node }) => (
  <div className="sdui-remote" data-module={String(node.module)}>
    [remote module: {String(node.module)} — Module Federation escape hatch]
  </div>
));

// ---------- helpers ----------
function gap(g: unknown): number | string | undefined {
  if (g === undefined || g === null) return undefined;
  if (typeof g === "number") return g;
  const tokens: Record<string, number> = { none: 0, xs: 4, sm: 8, md: 16, lg: 24, xl: 32 };
  return tokens[String(g)] ?? g as string;
}

function fmt(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

